import { useState, useEffect } from "react";
import {
  Button,
  Box,
  Typography,
  Paper,
  Link,
  useTheme,
  Alert,
  CircularProgress
} from "@mui/material";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../services/api.js';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function ResetPassword() {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [success, setSuccess] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    // Verificar se há token na URL
    if (!token) {
      setVerifying(false);
      setMessage({
        type: 'error',
        text: 'Token não encontrado na URL. Verifique o link recebido por e-mail.'
      });
      return;
    }

    // Verificar se o token é válido
    const verifyToken = async () => {
      try {
        const response = await api.get(`/auth/verify-reset-token/${token}`);
        
        if (response.data.valido) {
          // Token válido, processar reset
          await handleResetPassword();
        } else {
          setVerifying(false);
          setMessage({
            type: 'error',
            text: response.data.mensagem || 'Token inválido ou expirado.'
          });
        }
      } catch (error) {
        setVerifying(false);
        setMessage({
          type: 'error',
          text: error.response?.data?.mensagem || 'Erro ao verificar token. O link pode estar expirado ou inválido.'
        });
      }
    };

    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleResetPassword = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setVerifying(false);
      
      const response = await api.post('/auth/reset-password', {
        token: token
      });

      if (response.data.status === 'sucesso') {
        setSuccess(true);
        setMessage({
          type: 'success',
          text: response.data.mensagem || 'Senha redefinida com sucesso! Verifique seu e-mail para obter a nova senha.'
        });
      } else {
        setMessage({
          type: 'error',
          text: response.data.mensagem || 'Erro ao processar solicitação.'
        });
      }
    } catch (error) {
      console.error('Erro ao resetar senha:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.mensagem || 'Erro ao processar solicitação. Tente novamente mais tarde.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        padding: 2,
        position: 'relative'
      }}
    >
      <Paper
        elevation={8}
        sx={{
          padding: 4,
          borderRadius: 3,
          maxWidth: 500,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {success ? (
          <>
            <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />
            <Typography variant="h5" component="h1" gutterBottom>
              Senha Redefinida!
            </Typography>
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {message.text}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              Uma nova senha foi gerada e enviada para o seu e-mail cadastrado.
              Faça login com a nova senha e recomendamos alterá-la após o primeiro acesso.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => navigate('/login')}
              sx={{ mt: 2, py: 1.5 }}
            >
              Ir para Login
            </Button>
          </>
        ) : (
          <>
            {verifying || loading ? (
              <>
                <LockOutlinedIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h5" component="h1" gutterBottom>
                  Redefinindo Senha
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    {verifying ? 'Verificando token...' : 'Processando...'}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <ErrorIcon sx={{ fontSize: 60, color: '#f44336', mb: 2 }} />
                <Typography variant="h5" component="h1" gutterBottom>
                  Erro ao Redefinir Senha
                </Typography>
                {message.text && (
                  <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                    {message.text}
                  </Alert>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                  O link de recuperação pode ter expirado ou já foi utilizado.
                  Solicite um novo link de recuperação de senha.
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/forgot-password')}
                  sx={{ mt: 2, py: 1.5 }}
                >
                  Solicitar Novo Link
                </Button>
              </>
            )}
          </>
        )}

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/login')}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
          >
            <ArrowBackIcon fontSize="small" />
            Voltar para o login
          </Link>
        </Box>
      </Paper>
      
      {/* Rodapé */}
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        textAlign: 'center',
        padding: '12px',
        fontSize: '0.875rem',
        zIndex: 2
      }}>
        <Typography variant="body2">
          Desenvolvido por SAOTech Software. Tel. (11) 9 2039-2120 - suporte@saoinformatica.com.br
        </Typography>
      </Box>
    </Box>
  );
}

